import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ErreurApi, EVENEMENT_SESSION_EXPIREE, appelApi, urlApi } from './api';
import type { ChoixPeriode } from './useDashboardDirection';

/** Vue globale de la direction (TF-426) : apprenants, formateurs, formations. */
export type ListeVueGlobale = 'apprenants' | 'formateurs' | 'formations';

export interface DateLue {
  jour: string;
  provenance: string | null;
}

export interface LigneApprenantGlobale {
  id: string;
  nom: string;
  prenom: string;
  initiales: string;
  email: string | null;
  telephone: string | null;
  dateNaissance: string | null;
  age: number | null;
  siteCode: string;
  formation: { code: string; titre: string } | null;
  promotion: { cle: string; libelle: string };
  mode: 'CFA' | 'OF';
  statut: string;
  statutLibelle: string;
  statutTon: string;
  enFormation: boolean;
  debut: DateLue | null;
  fin: DateLue | null;
  examen: DateLue | null;
  contrat: { entreprise: string; maitreApprentissage: string | null; etat: string; libelle: string; ton: string } | null;
  referent: { id: string; nom: string } | null;
  conseiller: string | null;
  financement: { libelle: string; ton: string; detail: string | null } | null;
  assiduite: {
    tauxPresence: number | null;
    emargees: number;
    absencesNonJustifiees: number;
    seuil: number;
    seuilAtteint: boolean;
  };
  pieces: { deposees: number; attendues: number };
  mineur: boolean;
  rqth: boolean;
}

export interface ReponseApprenantsGlobale {
  calculeLe: string;
  apprenants: LigneApprenantGlobale[];
  total: number;
  page: number;
  taille: number;
  compteurs: { dossiers: number; enFormation: number; sousContrat: number; sansReferent: number; seuilAtteint: number };
  options: {
    formations: { code: string; titre: string }[];
    promotions: { cle: string; libelle: string; siteCode: string }[];
    referents: { id: string; nom: string }[];
    statuts: { statut: string; libelle: string; dossiers: number }[];
  };
}

export interface PeriodeVueGlobale {
  choix: ChoixPeriode;
  du: string;
  au: string;
  libelle: string;
}

export interface LigneFormateurGlobale {
  id: string;
  formateurId: string | null;
  nom: string;
  prenom: string;
  initiales: string;
  email: string;
  telephone: string | null;
  actif: boolean;
  sites: string[];
  statut: 'SALARIE' | 'INDEPENDANT' | null;
  statutLibelle: string;
  tauxHoraire: { centimes: number } | null;
  siret: string | null;
  apprenants: { referents: number; enFormation: number };
  activite: { demiJournees: number; minutes: number; feuillesOuvertes: number };
  fiches: { horsDelai: number; aValider: number };
  pieces: { aVerifier: number; nonConformes: number };
  facturesAVerifier: number;
  declarations: { enAttente: number; mois: string[] };
  aSuivre: boolean;
}

export interface ReponseFormateursGlobale {
  calculeLe: string;
  periode: PeriodeVueGlobale;
  filtres: { periodes: { valeur: ChoixPeriode; libelle: string }[] };
  formateurs: LigneFormateurGlobale[];
  total: number;
  compteurs: { formateurs: number; sansFiche: number; minutes: number; fichesHorsDelai: number; declarationsEnAttente: number };
}

export interface LigneFormationGlobale {
  id: string;
  code: string;
  titre: string;
  niveau: string;
  rncp: string;
  codeDiplome: string;
  actif: boolean;
  heures: number;
  dureeAnnees: number | null;
  seuilAbsences: number;
  antennes: string[];
  effectifs: { dossiers: number; enFormation: number; termines: number; ruptures: number };
  promotions: number;
  sessions: { enCours: number; aVenir: number };
  activite: { minutes: number; demiJournees: number; tauxPresence: number | null };
}

export interface ReponseFormationsGlobale {
  calculeLe: string;
  periode: PeriodeVueGlobale;
  filtres: { periodes: { valeur: ChoixPeriode; libelle: string }[]; niveaux: string[] };
  formations: LigneFormationGlobale[];
  total: number;
  compteurs: { actives: number; enFormation: number; sessionsEnCours: number; minutes: number };
}

/**
 * Paramètres d'adresse d'une liste : les mêmes pour la liste et pour son
 * export, qui sort ce que l'écran affiche. Une valeur vide n'est pas envoyée.
 */
export function parametresVueGlobale(filtres: Record<string, string | number | boolean | undefined>): string {
  const parametres = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(filtres)) {
    if (valeur === undefined || valeur === '' || valeur === false) continue;
    if (cle === 'site' && valeur === 'TOUS') continue;
    parametres.set(cle, String(valeur));
  }
  return parametres.toString();
}

/** Une période libre incomplète ou à l'envers n'est pas demandée. */
function periodeIncomplete(filtres: Record<string, string | number | boolean | undefined>): boolean {
  if (filtres.periode !== 'LIBRE') return false;
  return !filtres.du || !filtres.au || String(filtres.du) > String(filtres.au);
}

function useListe<T>(liste: ListeVueGlobale, filtres: Record<string, string | number | boolean | undefined>) {
  const chaine = parametresVueGlobale(filtres);
  return useQuery({
    queryKey: ['vue-globale', liste, chaine],
    queryFn: () => appelApi<T>(`/vue-globale/${liste}?${chaine}`),
    enabled: !periodeIncomplete(filtres),
    placeholderData: keepPreviousData,
  });
}

export function useApprenantsGlobale(filtres: Record<string, string | number | boolean | undefined>) {
  return useListe<ReponseApprenantsGlobale>('apprenants', filtres);
}

export function useFormateursGlobale(filtres: Record<string, string | number | boolean | undefined>) {
  return useListe<ReponseFormateursGlobale>('formateurs', filtres);
}

export function useFormationsGlobale(filtres: Record<string, string | number | boolean | undefined>) {
  return useListe<ReponseFormationsGlobale>('formations', filtres);
}

/**
 * Télécharge l'export Excel de la liste filtrée. Il passe par `fetch` plutôt
 * que par un lien : un refus de l'API (rien à exporter, liste trop longue)
 * doit s'afficher à l'écran, pas s'enregistrer comme un faux fichier. Rend le
 * nombre de lignes du fichier.
 */
export async function exporterVueGlobale(
  liste: ListeVueGlobale,
  filtres: Record<string, string | number | boolean | undefined>,
  corps: { sensibles?: boolean } = {},
): Promise<{ nomFichier: string; lignes: number }> {
  const sansPagination = { ...filtres, page: undefined, taille: undefined };
  const reponse = await fetch(urlApi(`/vue-globale/${liste}/export?${parametresVueGlobale(sansPagination)}`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(corps),
  });
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as
      | { erreur?: { code?: string; message?: string; details?: unknown } }
      | null;
    if (reponse.status === 401) window.dispatchEvent(new Event(EVENEMENT_SESSION_EXPIREE));
    throw new ErreurApi(
      reponse.status,
      erreur?.erreur?.code ?? 'ERREUR',
      erreur?.erreur?.message ?? 'L’export n’a pas pu être produit.',
      erreur?.erreur?.details,
    );
  }
  const nomFichier =
    /filename="([^"]+)"/.exec(reponse.headers.get('content-disposition') ?? '')?.[1] ?? `vue-globale-${liste}.xlsx`;
  const url = URL.createObjectURL(await reponse.blob());
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  document.body.appendChild(lien);
  lien.click();
  document.body.removeChild(lien);
  URL.revokeObjectURL(url);
  return { nomFichier, lignes: Number(reponse.headers.get('x-lignes') ?? 0) };
}
